import React, { useEffect, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import analytics from './config';
import { trackPageView, identifyUser, trackNavigationClicked, NavigationClickedProperties } from './events';
import { getSessionId } from './utils/cookies';
import { RootState } from '@/store';

interface AnalyticsContextType {
    analytics: typeof analytics;
    trackPageView: typeof trackPageView;
    identifyUser: typeof identifyUser;
    trackNavigationClicked: typeof trackNavigationClicked;
}

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
};

interface AnalyticsProviderProps {
    children: React.ReactNode;
}

const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
    const location = useLocation();
    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

    // Initialize session
    useEffect(() => {
        getSessionId(); // This will create a session ID if one doesn't exist
    }, []);

    // Track page views on route changes
    useEffect(() => {
        const pageName = getPageName(location.pathname);
        trackPageView(pageName, {
            path: location.pathname,
            search: location.search,
            hash: location.hash,
        });
        trackNavigationClicked({
            navigation_item_id: `nav_${location.pathname.slice(1)}`,
            navigation_item_name: pageName,
            target_url: location.pathname,
        });
    }, [location]);

    // Identify user when authenticated
    useEffect(() => {
        if (isAuthenticated && user) {
            identifyUser(user.id, {
                email: user.email,
                firstName: user.first_name,
                lastName: user.last_name,
                role: '',
                createdAt: user.created_at,
            });
        }
    }, [isAuthenticated, user]);

    // Track performance metrics
    useEffect(() => {
        // Track page load performance
        if (typeof window !== 'undefined' && 'performance' in window) {
            try {
                const observer = new PerformanceObserver((list) => {
                    list.getEntries().forEach((entry) => {
                        if (entry.entryType === 'navigation') {
                            const navEntry = entry as PerformanceNavigationTiming;
                            try {
                                analytics.track('page_performance', {
                                    loadTime: navEntry.loadEventEnd - navEntry.loadEventStart,
                                    domContentLoaded:
                                        navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
                                    firstContentfulPaint: navEntry.loadEventEnd - navEntry.fetchStart,
                                    category: 'performance',
                                });
                            } catch (error) {
                                console.error('Failed to track performance metrics:', error);
                            }
                        }
                    });
                });

                observer.observe({ entryTypes: ['navigation'] });

                return () => observer.disconnect();
            } catch (error) {
                console.error('Failed to initialize performance observer:', error);
            }
        }
    }, []);

    // Track errors
    useEffect(() => {
        const handleError = (event: ErrorEvent) => {
            try {
                analytics.track('javascript_error', {
                    message: event.message,
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                    stack: event.error?.stack,
                    category: 'error',
                });
            } catch (error) {
                console.error('Failed to track JavaScript error:', error);
            }
        };

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            try {
                analytics.track('unhandled_promise_rejection', {
                    reason: event.reason?.toString(),
                    category: 'error',
                });
            } catch (error) {
                console.error('Failed to track unhandled promise rejection:', error);
            }
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
        };
    }, []);

    const contextValue: AnalyticsContextType = {
        analytics,
        trackPageView,
        identifyUser,
    };

    return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};

// Helper function to get readable page names
const getPageName = (pathname: string): string => {
    const routes: Record<string, string> = {
        '/': 'Landing Page',
        '/landing-test': 'Assessment Test',
        '/landing-test-result': 'Assessment Result',
        '/auth/login': 'Login',
        '/auth/register': 'Register',
        '/student/dashboard': 'Student Dashboard',
        '/student/vocabulary': 'Vocabulary',
        '/student/exercise': 'Exercise',
        '/student/speaking': 'Speaking Practice',
        '/student/lessons': 'Lessons',
        '/student/progress': 'Progress',
        '/student/achievements': 'Achievements',
        '/student/settings': 'Settings',
        '/student/profile': 'Profile',
    };

    // Check for exact matches first
    if (routes[pathname]) {
        return routes[pathname];
    }

    // Check for pattern matches
    if (pathname.startsWith('/student/')) {
        return `Student - ${pathname.split('/').pop()?.replace('-', ' ') || 'Unknown'}`;
    }

    if (pathname.startsWith('/auth/')) {
        return `Auth - ${pathname.split('/').pop()?.replace('-', ' ') || 'Unknown'}`;
    }

    // Default fallback
    return pathname.split('/').filter(Boolean).join(' > ') || 'Home';
};

export default AnalyticsProvider;
